import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  ListVideo, Plus, Pencil, Trash2, RefreshCw, Play, Calendar, Clock, AlertTriangle,
  ArrowLeft, Monitor, MapPin, GripVertical, X, Eye, Zap
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHEDULE_TYPES = [
  { value: "always_on", label: "Always On" },
  { value: "date_range", label: "Date Range" },
  { value: "daypart", label: "Time of Day" }
];

const DAYS_OF_WEEK = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" }
];

export default function AdminPlaylists() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playlists, setPlaylists] = useState([]);
  const [sites, setSites] = useState([]);
  const [screens, setScreens] = useState([]);
  const [content, setContent] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [filterSite, setFilterSite] = useState("all");
  const [activeTab, setActiveTab] = useState("playlists");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showEmergencyDialog, setShowEmergencyDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [previewPlaylist, setPreviewPlaylist] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    site_id: "",
    is_default: false,
    items: [],
    schedule: {
      schedule_type: "always_on",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      days_of_week: [0, 1, 2, 3, 4, 5, 6]
    }
  });
  
  const [assignmentData, setAssignmentData] = useState({
    target_type: "screen",
    screen_id: "",
    site_id: "",
    playlist_id: "",
    priority: 0,
    schedule: {
      schedule_type: "always_on",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      days_of_week: [0, 1, 2, 3, 4, 5, 6]
    }
  });
  
  const [emergencyData, setEmergencyData] = useState({
    target_type: "site",
    target_id: "",
    playlist_id: "",
    message: "",
    expires_hours: 24
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return true;
      }
    } catch (err) {
      console.error("Auth check failed:", err);
    }
    navigate("/admin/login");
    return false;
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [sitesRes, screensRes, contentRes, playlistsRes, assignmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/sites`, { credentials: "include" }),
        fetch(`${API_URL}/api/screens`, { credentials: "include" }),
        fetch(`${API_URL}/api/content?status=active`, { credentials: "include" }),
        fetch(`${API_URL}/api/playlists${filterSite !== 'all' ? `?site_id=${filterSite}` : ''}`, { credentials: "include" }),
        fetch(`${API_URL}/api/assignments`, { credentials: "include" })
      ]);
      
      if (sitesRes.ok) setSites(await sitesRes.json());
      if (screensRes.ok) setScreens(await screensRes.json());
      if (contentRes.ok) setContent(await contentRes.json());
      if (playlistsRes.ok) setPlaylists(await playlistsRes.json());
      if (assignmentsRes.ok) setAssignments(await assignmentsRes.json());
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  }, [filterSite]);

  useEffect(() => {
    const init = async () => {
      const authed = await checkAuth();
      if (authed) {
        setLoading(false);
        await fetchData();
      }
    };
    init();
  }, [checkAuth, fetchData]);

  useEffect(() => {
    if (user) fetchData();
  }, [filterSite, user, fetchData]);

  const handleCreatePlaylist = async () => {
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        site_id: formData.site_id || null,
        schedule: formData.schedule.schedule_type === "always_on" ? { schedule_type: "always_on" } : formData.schedule
      };
      
      const res = await fetch(`${API_URL}/api/playlists`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Playlist created successfully");
        setShowCreateDialog(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create playlist");
      }
    } catch (err) {
      toast.error("Failed to create playlist");
    }
  };

  const handleUpdatePlaylist = async () => {
    if (!editingPlaylist) return;
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        site_id: formData.site_id || null,
        is_default: formData.is_default,
        items: formData.items,
        schedule: formData.schedule.schedule_type === "always_on" ? { schedule_type: "always_on" } : formData.schedule
      };
      
      const res = await fetch(`${API_URL}/api/playlists/${editingPlaylist.playlist_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Playlist updated successfully");
        setShowEditDialog(false);
        setEditingPlaylist(null);
        resetForm();
        fetchData();
      } else {
        toast.error("Failed to update playlist");
      }
    } catch (err) {
      toast.error("Failed to update playlist");
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm("Are you sure you want to deactivate this playlist?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/playlists/${playlistId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Playlist deactivated");
        fetchData();
      } else {
        toast.error("Failed to deactivate playlist");
      }
    } catch (err) {
      toast.error("Failed to deactivate playlist");
    }
  };

  const handleCreateAssignment = async () => {
    if (!assignmentData.playlist_id) {
      toast.error("Playlist is required");
      return;
    }
    if (assignmentData.target_type === "screen" && !assignmentData.screen_id) {
      toast.error("Screen is required");
      return;
    }
    if (assignmentData.target_type === "site" && !assignmentData.site_id) {
      toast.error("Site is required");
      return;
    }
    
    try {
      const payload = {
        playlist_id: assignmentData.playlist_id,
        priority: assignmentData.priority,
        schedule: assignmentData.schedule.schedule_type === "always_on" ? { schedule_type: "always_on" } : assignmentData.schedule
      };
      
      if (assignmentData.target_type === "screen") {
        payload.screen_id = assignmentData.screen_id;
      } else {
        payload.site_id = assignmentData.site_id;
      }
      
      const res = await fetch(`${API_URL}/api/assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Assignment created");
        setShowAssignDialog(false);
        resetAssignmentData();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create assignment");
      }
    } catch (err) {
      toast.error("Failed to create assignment");
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/assignments/${assignmentId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Assignment removed");
        fetchData();
      } else {
        toast.error("Failed to remove assignment");
      }
    } catch (err) {
      toast.error("Failed to remove assignment");
    }
  };

  const handleCreateEmergency = async () => {
    if (!emergencyData.target_id || !emergencyData.playlist_id) {
      toast.error("Target and playlist are required");
      return;
    }
    
    try {
      const endpoint = emergencyData.target_type === "site"
        ? `${API_URL}/api/emergency/site/${emergencyData.target_id}`
        : `${API_URL}/api/emergency/screen/${emergencyData.target_id}`;
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          playlist_id: emergencyData.playlist_id,
          message: emergencyData.message || "Emergency Alert",
          expires_hours: emergencyData.expires_hours
        })
      });
      
      if (res.ok) {
        toast.success("Emergency override activated");
        setShowEmergencyDialog(false);
        resetEmergencyData();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to activate emergency");
      }
    } catch (err) {
      toast.error("Failed to activate emergency");
    }
  };

  const handleCancelEmergency = async (targetType, targetId) => {
    try {
      const endpoint = targetType === "site"
        ? `${API_URL}/api/emergency/site/${targetId}`
        : `${API_URL}/api/emergency/screen/${targetId}`;
      
      const res = await fetch(endpoint, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Emergency override cancelled");
        fetchData();
      } else {
        toast.error("Failed to cancel emergency");
      }
    } catch (err) {
      toast.error("Failed to cancel emergency");
    }
  };

  const openEditDialog = (playlist) => {
    setEditingPlaylist(playlist);
    setFormData({
      name: playlist.name,
      description: playlist.description || "",
      site_id: playlist.site_id || "",
      is_default: playlist.is_default,
      items: playlist.items || [],
      schedule: playlist.schedule || { schedule_type: "always_on" }
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = async (playlist) => {
    try {
      const res = await fetch(`${API_URL}/api/playlists/${playlist.playlist_id}/preview`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPreviewPlaylist(data);
        setShowPreviewDialog(true);
      }
    } catch (err) {
      toast.error("Failed to load preview");
    }
  };

  const addContentToPlaylist = (contentId) => {
    const contentItem = content.find(c => c.content_id === contentId);
    if (!contentItem) return;
    
    const newItem = {
      content_id: contentId,
      order: formData.items.length,
      duration_seconds: contentItem.default_duration_seconds || 10
    };
    
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const removeItemFromPlaylist = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    // Reorder
    newItems.forEach((item, i) => item.order = i);
    setFormData({ ...formData, items: newItems });
  };

  const updateItemDuration = (index, duration) => {
    const newItems = [...formData.items];
    newItems[index].duration_seconds = parseInt(duration) || 10;
    setFormData({ ...formData, items: newItems });
  };

  const moveItem = (index, direction) => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === formData.items.length - 1) return;
    
    const newItems = [...formData.items];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    newItems.forEach((item, i) => item.order = i);
    setFormData({ ...formData, items: newItems });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      site_id: "",
      is_default: false,
      items: [],
      schedule: { schedule_type: "always_on" }
    });
  };

  const resetAssignmentData = () => {
    setAssignmentData({
      target_type: "screen",
      screen_id: "",
      site_id: "",
      playlist_id: "",
      priority: 0,
      schedule: { schedule_type: "always_on" }
    });
  };

  const resetEmergencyData = () => {
    setEmergencyData({
      target_type: "site",
      target_id: "",
      playlist_id: "",
      message: "",
      expires_hours: 24
    });
  };

  const getSiteName = (siteId) => sites.find(s => s.site_id === siteId)?.name || siteId || "Global";
  const getContentName = (contentId) => content.find(c => c.content_id === contentId)?.name || contentId;
  const getPlaylistName = (playlistId) => playlists.find(p => p.playlist_id === playlistId)?.name || playlistId;
  const getScreenName = (screenId) => screens.find(s => s.screen_id === screenId)?.name || screenId;

  const activeEmergencies = assignments.filter(a => a.is_emergency && a.is_active);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <ListVideo className="h-6 w-6 text-emerald-400" />
                Playlists & Scheduling
              </h1>
              <p className="text-sm text-slate-400">Manage content playlists and screen assignments</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEmergencyDialog(true)} className="border-red-500 text-red-400 hover:bg-red-500/20">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Emergency Override
            </Button>
            <Button onClick={() => setShowAssignDialog(true)} variant="outline">
              <Monitor className="h-4 w-4 mr-2" />
              Assign to Screen
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              New Playlist
            </Button>
          </div>
        </div>
      </header>

      {/* Emergency Alert Banner */}
      {activeEmergencies.length > 0 && (
        <div className="bg-red-900/50 border-b border-red-700 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <span className="font-medium text-red-200">
                {activeEmergencies.length} active emergency override{activeEmergencies.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex gap-2">
              {activeEmergencies.map(e => (
                <Badge key={e.assignment_id} variant="destructive" className="cursor-pointer" onClick={() => handleCancelEmergency(e.site_id ? "site" : "screen", e.site_id || e.screen_id)}>
                  {e.site_name || e.screen_name} - Click to cancel
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            <TabsList>
              <TabsTrigger value="playlists">Playlists</TabsTrigger>
              <TabsTrigger value="assignments">Assignments</TabsTrigger>
            </TabsList>
            
            <div className="flex gap-4">
              <Select value={filterSite} onValueChange={setFilterSite}>
                <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Filter by site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Playlists Tab */}
          <TabsContent value="playlists">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playlists.map(playlist => (
                <Card key={playlist.playlist_id} className={`bg-slate-800 border-slate-700 ${!playlist.is_active ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ListVideo className="h-5 w-5 text-slate-400" />
                          {playlist.name}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {getSiteName(playlist.site_id)}
                        </CardDescription>
                      </div>
                      <div className="flex gap-1">
                        {playlist.is_default && <Badge className="bg-blue-500">Default</Badge>}
                        <Badge variant={playlist.is_active ? "default" : "secondary"}>
                          {playlist.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-400 mb-4">
                      <p>{playlist.items?.length || 0} items</p>
                      <p className="flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {playlist.schedule?.schedule_type === "always_on" ? "Always on" :
                         playlist.schedule?.schedule_type === "date_range" ? "Date range" : "Daypart"}
                      </p>
                    </div>
                    
                    {playlist.description && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{playlist.description}</p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPreviewDialog(playlist)}>
                        <Eye className="h-3 w-3 mr-1" />
                        Preview
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(playlist)}>
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      {playlist.is_active && (
                        <Button size="sm" variant="destructive" onClick={() => handleDeletePlaylist(playlist.playlist_id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {playlists.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <ListVideo className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No playlists found</p>
                <p className="text-sm">Create a playlist to organize your content</p>
              </div>
            )}
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <div className="space-y-4">
              {assignments.filter(a => !a.is_emergency).map(assignment => (
                <Card key={assignment.assignment_id} className="bg-slate-800 border-slate-700">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {assignment.screen_id ? (
                          <Monitor className="h-8 w-8 text-blue-400" />
                        ) : (
                          <MapPin className="h-8 w-8 text-emerald-400" />
                        )}
                        <div>
                          <p className="font-medium">
                            {assignment.screen_id ? assignment.screen_name : `All screens at ${assignment.site_name}`}
                          </p>
                          <p className="text-sm text-slate-400">
                            Playlist: {assignment.playlist_name} • Priority: {assignment.priority}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {assignment.schedule?.schedule_type === "always_on" ? "Always on" :
                           assignment.schedule?.schedule_type === "date_range" ? "Date range" : "Daypart"}
                        </Badge>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteAssignment(assignment.assignment_id)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {assignments.filter(a => !a.is_emergency).length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No screen assignments</p>
                  <p className="text-sm">Assign playlists to screens or sites</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Playlist Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => { if (!open) { setShowCreateDialog(false); setShowEditDialog(false); setEditingPlaylist(null); resetForm(); } }}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlaylist ? "Edit Playlist" : "Create New Playlist"}</DialogTitle>
            <DialogDescription>Build your content playlist</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Playlist Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Welcome Loop"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Site (optional)</Label>
                <Select value={formData.site_id} onValueChange={(v) => setFormData({ ...formData, site_id: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Global (all sites)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Global (all sites)</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_default">Set as default playlist for site</Label>
            </div>

            {/* Schedule */}
            <div className="p-4 bg-slate-700 rounded-lg">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Schedule
              </h4>
              
              <div className="space-y-4">
                <div>
                  <Label>Schedule Type</Label>
                  <Select
                    value={formData.schedule.schedule_type}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, schedule_type: v }
                    })}
                  >
                    <SelectTrigger className="bg-slate-600 border-slate-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.schedule.schedule_type === "date_range" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.schedule.start_date}
                        onChange={(e) => setFormData({
                          ...formData,
                          schedule: { ...formData.schedule, start_date: e.target.value }
                        })}
                        className="bg-slate-600 border-slate-500"
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={formData.schedule.end_date}
                        onChange={(e) => setFormData({
                          ...formData,
                          schedule: { ...formData.schedule, end_date: e.target.value }
                        })}
                        className="bg-slate-600 border-slate-500"
                      />
                    </div>
                  </div>
                )}
                
                {formData.schedule.schedule_type === "daypart" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={formData.schedule.start_time}
                          onChange={(e) => setFormData({
                            ...formData,
                            schedule: { ...formData.schedule, start_time: e.target.value }
                          })}
                          className="bg-slate-600 border-slate-500"
                        />
                      </div>
                      <div>
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={formData.schedule.end_time}
                          onChange={(e) => setFormData({
                            ...formData,
                            schedule: { ...formData.schedule, end_time: e.target.value }
                          })}
                          className="bg-slate-600 border-slate-500"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Days of Week</Label>
                      <div className="flex gap-2 mt-2">
                        {DAYS_OF_WEEK.map(day => (
                          <Button
                            key={day.value}
                            size="sm"
                            variant={formData.schedule.days_of_week?.includes(day.value) ? "default" : "outline"}
                            onClick={() => {
                              const days = formData.schedule.days_of_week || [];
                              const newDays = days.includes(day.value)
                                ? days.filter(d => d !== day.value)
                                : [...days, day.value];
                              setFormData({
                                ...formData,
                                schedule: { ...formData.schedule, days_of_week: newDays }
                              });
                            }}
                          >
                            {day.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Content Items */}
            <div className="p-4 bg-slate-700 rounded-lg">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <Play className="h-4 w-4" />
                Playlist Items
              </h4>
              
              {/* Add Content */}
              <div className="mb-4">
                <Label>Add Content</Label>
                <Select onValueChange={addContentToPlaylist}>
                  <SelectTrigger className="bg-slate-600 border-slate-500">
                    <SelectValue placeholder="Select content to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    {content.map(c => (
                      <SelectItem key={c.content_id} value={c.content_id}>
                        {c.name} ({c.content_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Items List */}
              <div className="space-y-2">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-slate-600 rounded">
                    <GripVertical className="h-4 w-4 text-slate-400" />
                    <span className="flex-1">{index + 1}. {getContentName(item.content_id)}</span>
                    <Input
                      type="number"
                      value={item.duration_seconds}
                      onChange={(e) => updateItemDuration(index, e.target.value)}
                      className="w-20 h-8 bg-slate-500 border-slate-400"
                    />
                    <span className="text-sm text-slate-400">sec</span>
                    <Button size="sm" variant="ghost" onClick={() => moveItem(index, "up")} disabled={index === 0}>↑</Button>
                    <Button size="sm" variant="ghost" onClick={() => moveItem(index, "down")} disabled={index === formData.items.length - 1}>↓</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeItemFromPlaylist(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              {formData.items.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No items added yet</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); setEditingPlaylist(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={editingPlaylist ? handleUpdatePlaylist : handleCreatePlaylist} className="bg-emerald-600 hover:bg-emerald-700">
              {editingPlaylist ? "Save Changes" : "Create Playlist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Assign Playlist to Screen</DialogTitle>
            <DialogDescription>Schedule content to play on screens</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Target Type</Label>
              <Select value={assignmentData.target_type} onValueChange={(v) => setAssignmentData({ ...assignmentData, target_type: v, screen_id: "", site_id: "" })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="screen">Specific Screen</SelectItem>
                  <SelectItem value="site">All Screens at Site</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {assignmentData.target_type === "screen" ? (
              <div>
                <Label>Screen *</Label>
                <Select value={assignmentData.screen_id} onValueChange={(v) => setAssignmentData({ ...assignmentData, screen_id: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select screen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {screens.map(screen => (
                      <SelectItem key={screen.screen_id} value={screen.screen_id}>
                        {screen.name} ({getSiteName(screen.site_id)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Site *</Label>
                <Select value={assignmentData.site_id} onValueChange={(v) => setAssignmentData({ ...assignmentData, site_id: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select site..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(site => (
                      <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Playlist *</Label>
              <Select value={assignmentData.playlist_id} onValueChange={(v) => setAssignmentData({ ...assignmentData, playlist_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select playlist..." />
                </SelectTrigger>
                <SelectContent>
                  {playlists.filter(p => p.is_active).map(playlist => (
                    <SelectItem key={playlist.playlist_id} value={playlist.playlist_id}>{playlist.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Priority (higher = takes precedence)</Label>
              <Input
                type="number"
                value={assignmentData.priority}
                onChange={(e) => setAssignmentData({ ...assignmentData, priority: parseInt(e.target.value) || 0 })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Schedule</Label>
              <Select
                value={assignmentData.schedule.schedule_type}
                onValueChange={(v) => setAssignmentData({
                  ...assignmentData,
                  schedule: { ...assignmentData.schedule, schedule_type: v }
                })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignDialog(false); resetAssignmentData(); }}>Cancel</Button>
            <Button onClick={handleCreateAssignment} className="bg-emerald-600 hover:bg-emerald-700">Create Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Override Dialog */}
      <Dialog open={showEmergencyDialog} onOpenChange={setShowEmergencyDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Emergency Override
            </DialogTitle>
            <DialogDescription>Immediately override content on screens</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Target Type</Label>
              <Select value={emergencyData.target_type} onValueChange={(v) => setEmergencyData({ ...emergencyData, target_type: v, target_id: "" })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="site">All Screens at Site</SelectItem>
                  <SelectItem value="screen">Specific Screen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Target *</Label>
              <Select value={emergencyData.target_id} onValueChange={(v) => setEmergencyData({ ...emergencyData, target_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder={`Select ${emergencyData.target_type}...`} />
                </SelectTrigger>
                <SelectContent>
                  {emergencyData.target_type === "site"
                    ? sites.map(site => <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>)
                    : screens.map(screen => <SelectItem key={screen.screen_id} value={screen.screen_id}>{screen.name}</SelectItem>)
                  }
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Emergency Playlist *</Label>
              <Select value={emergencyData.playlist_id} onValueChange={(v) => setEmergencyData({ ...emergencyData, playlist_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select playlist..." />
                </SelectTrigger>
                <SelectContent>
                  {playlists.filter(p => p.is_active).map(playlist => (
                    <SelectItem key={playlist.playlist_id} value={playlist.playlist_id}>{playlist.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Emergency Message</Label>
              <Input
                value={emergencyData.message}
                onChange={(e) => setEmergencyData({ ...emergencyData, message: e.target.value })}
                placeholder="e.g., Please evacuate calmly"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Auto-expire after (hours)</Label>
              <Input
                type="number"
                value={emergencyData.expires_hours}
                onChange={(e) => setEmergencyData({ ...emergencyData, expires_hours: parseInt(e.target.value) || 24 })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEmergencyDialog(false); resetEmergencyData(); }}>Cancel</Button>
            <Button onClick={handleCreateEmergency} className="bg-red-600 hover:bg-red-700">
              <Zap className="h-4 w-4 mr-2" />
              Activate Emergency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle>Playlist Preview: {previewPlaylist?.name}</DialogTitle>
            <DialogDescription>
              {previewPlaylist?.enriched_items?.length || 0} items • Total duration: {previewPlaylist?.total_duration_seconds}s
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {previewPlaylist?.enriched_items?.map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-slate-700 rounded">
                <span className="text-lg font-bold text-slate-400">{index + 1}</span>
                <div className="flex-1">
                  <p className="font-medium">{item.content_name}</p>
                  <p className="text-sm text-slate-400">{item.content_type}{item.widget_type ? ` - ${item.widget_type}` : ''}</p>
                </div>
                <Badge variant="outline">{item.effective_duration}s</Badge>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
